import { CalendarOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Switch, Tooltip, Typography } from 'antd'
import { countries } from 'countries-list'
import * as PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useSelectedBranch } from '../../../../context/branchContext'
import { useUser } from '../../../../context/userContext'
import { currencyHelper } from '../../../../shared/utils/currencies'
import { genders, maritalStatuses, patientJobs, visiblePatientFields as patientFields } from '../../../../shared/utils/helpers'
import { patientRelations, patientTitles } from '../../../../shared/utils/medicationsHelpers'
import { DASHBOARD_HOME } from '../../../../variables'
import AddressField from '../../../common/formFields/AddressField'
import CountryField from '../../../common/formFields/CountryField'
import EmailField from '../../../common/formFields/EmailField'
import NameField from '../../../common/formFields/NameField'
import PhoneField from '../../../common/formFields/PhoneField'
import SelectWithNewOptions from '../../../common/formFields/SelectWithNewOptions'
import SearchField from '../../../common/searchFields/SearchField'
import UploadFileButton from '../Patient/components/PatientFiles/UploadFileButton'
import InsuranceForm from './InsuranceForm'
import { selectorDateFormat } from '../../settings/Print/formHelpers'
import {
  SEARCH_BRANCHES_QUERY,
  SEARCH_PATIENT_QUERY,
  SEARCH_PRICE_LISTS_QUERY
} from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { BRANCH_DETAILS } from '@dentolize/clinic-mobile/src/shared/store/queries/companyQueries'
import EditReferenceIdButton from '../Patient/components/PatientProfile/EditReferenceIdButton'
import PatientFamilyForm from './PatientFamilyForm'
import NationalIdField, { getIdentifierFormatError } from '../../../common/formFields/NationalIdField'
import { DHSCheckInsurance, useFeatureFlag } from '@dentolize/clinic-web-canary'
import MoneyFormat from '../../../common/MoneyFormat'
import ReferralSourceField from './ReferralSourceField'
import SEARCH_PATIENT_TAGS from '@dentolize/clinic-mobile/src/shared/store/queries/actions/patientTags/searchPatientTags'
import moment from 'moment'
import MomentDatePicker from '../../../../utils/timezone/MomentDatePicker'
import dayjs from 'dayjs'

// A pending patient (no ID yet: added as one, or edited without being given one; a lead is given one when edited)
// has the clinic's own required fields for them, when it sets any; the QR forms pass theirs
export const patientRequiredFields = ({ user, selectedPatient, assignNewID, isTempPatient, requiredPatientFields }) => {
  const isPending = selectedPatient?.id
    ? selectedPatient.referenceId === 0 && selectedPatient.type !== 2 && !assignNewID
    : !!isTempPatient

  return (
    requiredPatientFields ||
    (isPending && user?.company.requiredPendingPatientFields?.length
      ? user.company.requiredPendingPatientFields
      : user?.company.requiredPatientFields) ||
    []
  )
}

const PatientCommonFields = props => {
  const {
    form,
    selectedPatient,
    autofocusInputRef,
    forPatientQrCode,
    phoneDisabled,
    companyCountry,
    formVisible = false,
    images,
    setImages,
    onReset,
    requiredPatientFields,
    visiblePatientFields = Object.keys(patientFields),
    companyId,
    debounceTime,
    generatedID,
    forceNationalIDCheck,
    assignNewID
  } = props
  const { setFieldsValue, getFieldValue, setFields, validateFields } = form

  const [isBirthdate, setIsBirthDate] = useState(selectedPatient?.id ? !!selectedPatient.birthDay : true)

  const { t } = useTranslation()
  const { selectedBranch } = useSelectedBranch()
  const { user } = useUser()
  const hasDhsFeature = useFeatureFlag('FEATURE_DHS_INTEGRATION')

  const [searchPatients] = useLazyQuery(SEARCH_PATIENT_QUERY)

  const longitude = Form.useWatch('longitude', form)
  const address = Form.useWatch('address', form)
  const identifierType = Form.useWatch('identifierType', form)
  const nationality = Form.useWatch('nationality', form)
  const nationalId = Form.useWatch('nationalId', form)
  const country = Form.useWatch('country', form) || companyCountry
  const firstName = Form.useWatch('firstName', form)
  const lastName = Form.useWatch('lastName', form)
  const gender = Form.useWatch('gender', form)
  const marital = Form.useWatch('marital', form)
  const job = Form.useWatch('job', form)
  const birthDate = Form.useWatch('birthDate', form)
  const isTempPatient = Form.useWatch('isTempPatient', form)

  const identifierFormatError = getIdentifierFormatError({
    value: nationalId,
    identifierType,
    nationality,
    country: user?.company?.country,
    companyCountry
  })

  const requiredFields = patientRequiredFields({ user, selectedPatient, assignNewID, isTempPatient, requiredPatientFields })
  const hasMiddleName = requiredFields.includes('middleNames')

  const [emergencyVisible, setEmergencyVisible] = useState(false)
  // a required emergency contact is always shown, so its fields are checked: the QR pages never open the form, which is
  // when it is opened below
  const emergencyShown = emergencyVisible || requiredFields.includes('emergencyContact')

  useEffect(() => {
    setIsBirthDate(
      requiredFields.includes('birthDate')
        ? true
        : selectedPatient?.id
        ? !!selectedPatient.birthDay
        : !requiredFields.includes('age')
    )
  }, [companyCountry, selectedPatient, formVisible])

  useEffect(() => {
    if (formVisible) {
      setEmergencyVisible(
        requiredFields.includes('emergencyContact') ||
          selectedPatient?.firstNameE ||
          selectedPatient?.lastNameE ||
          selectedPatient?.phoneNumberE ||
          selectedPatient?.phoneNumber2E ||
          selectedPatient?.addressE ||
          selectedPatient?.relationE
      )
    }
  }, [formVisible])

  const { data: branchData } = useQuery(BRANCH_DETAILS, {
    variables: { branch: selectedBranch },
    fetchPolicy: 'cache-first',
    skip: !user || !selectedBranch
  })

  useEffect(() => {
    if (nationalId) {
      validateFields(['nationalId'])
    }
  }, [identifierType, nationality])

  const handleSwitchAge = isBirthdate => {
    setIsBirthDate(isBirthdate)

    if (!isBirthdate) {
      const birthDate = getFieldValue('birthDate')

      if (birthDate) {
        setFieldsValue({
          age: moment().year() - moment(birthDate).year()
        })
      }
    }
  }

  const handleChangeAge = age => {
    setFieldsValue({
      birthDate: moment()
        .subtract(age, 'years')
        .startOf('year'),
      birthDay: null,
      age
    })
  }

  const handleChangeBirthDate = birthDate => {
    setFieldsValue({
      birthDay: birthDate ? `${moment(birthDate).date()}/${moment(birthDate).month()}` : null
    })
  }

  const handleCheckDuplicatePatients = async (e, searchCode) => {
    if (!user || forPatientQrCode || !e.target.value) return
    try {
      const suffix = getFieldValue('suffix') || ''
      const res = await searchPatients({
        variables: {
          searchTerm:
            searchCode === '@' ? `${searchCode}${suffix.trim()}${e.target.value.trim()}` : `${searchCode}${e.target.value}`,
          showArchived: true
        },
        fetchPolicy: 'cache-and-network'
      })

      if (res.data.searchPatients.filter(p => p.id !== selectedPatient.id && p.id !== generatedID).length && formVisible) {
        Modal.confirm({
          title: t('app.duplicatePatients'),
          content: (
            <>
              <Typography.Text strong>
                {t(searchCode === '@' ? 'app.duplicatePatientsText' : 'app.duplicatePatientsTextID', {
                  val: res.data.searchPatients.filter(p => p.id !== selectedPatient.id && p.id !== generatedID).length
                })}
              </Typography.Text>
              <ul>
                {res.data.searchPatients
                  .filter(p => p.id !== selectedPatient.id && p.id !== generatedID)
                  .map(p => (
                    <li key={p.id}>
                      <a
                        style={p.disabled ? { color: 'red' } : {}}
                        href={`${DASHBOARD_HOME}/patient/${p.id}`}
                        rel="noreferrer noopener"
                        target="_blank"
                      >{`${p.firstName} ${p.lastName}`}</a>
                    </li>
                  ))}
              </ul>
            </>
          ),
          okButtonProps: { danger: true },
          cancelButtonProps: { hidden: true }
        })
      }
    } catch (e) {
      // console.log(e);
    }
  }

  const handleInsuranceSelect = params => {
    const { coverage, resolvedCoverage, insuranceCompanyId, insuranceCompanyName } = params

    const updates = {}

    if (insuranceCompanyId) {
      updates.insurance = {
        key: insuranceCompanyId,
        value: insuranceCompanyId,
        label: insuranceCompanyName || 'Selected Insurance'
      }

      if (resolvedCoverage?.insurancePolicy?.id) {
        updates.insurancePolicy = {
          key: resolvedCoverage.insurancePolicy.id,
          value: resolvedCoverage.insurancePolicy.id,
          label: resolvedCoverage.policyNumber || 'Selected Policy'
        }
      } else {
        updates.insurancePolicy = null
      }

      if (resolvedCoverage?.policyClass?.id) {
        updates.policyClass = {
          key: resolvedCoverage.policyClass.id,
          value: resolvedCoverage.policyClass.id,
          label: resolvedCoverage.className || 'Selected Class'
        }
      } else {
        updates.policyClass = null
      }

      if (coverage?.beneficiaryNumber) {
        updates.insuranceNumber = coverage.beneficiaryNumber
      }

      setFieldsValue(updates)
    }
  }

  const colMediumSize = forPatientQrCode ? 12 : 8
  const nameMediumSize = hasMiddleName ? 4 : colMediumSize

  const { formatter, parser } = currencyHelper()

  const handleChangeCountry = country => {
    const code = countries[country]?.phone ? `+${countries[country]?.phone}` : null
    if (code) {
      const phoneNumber = getFieldValue('phoneNumber')
      if (!phoneNumber && code) {
        setFieldsValue({ suffix: code })
      }
      const phoneNumber2 = getFieldValue('phoneNumber2')
      if (!phoneNumber2 && code) {
        setFieldsValue({ suffix2: code })
      }
    }
  }

  // as the server sets it for whoever may not change taxes: the clinic's tax for other nationalities, none for its own
  const handleChangeNationality = nationality => {
    // A Saudi citizen's invoices are zero-rated (VATEX-SA-HEA); a profile left at 15% or unset
    // (the clinic's rate) starts each invoice at a rate it cannot be reported at
    if (user?.company.country === 'SA' && nationality === 'SA') {
      setFieldsValue({ tax: 0 })
    } else if (Number(user?.company.extraSettings.taxNonNationals)) {
      setFieldsValue({
        tax: nationality && nationality !== user.company.country ? Number(user.company.extraSettings.taxNonNationals) : null
      })
    }
  }

  const handlePatientDataChange = (data) => {
    const updates = {}

    if (data.firstName) updates.firstName = data.firstName
    if (data.lastName) updates.lastName = data.lastName
    if (data.birthDate) {
      updates.birthDate = dayjs(data.birthDate)
      setIsBirthDate(true)
    }
    if (data.nationalId) updates.nationalId = data.nationalId
    if (data.job) updates.job = data.job
    if (data.marital) updates.marital = data.marital
    if (data.gender) updates.gender = data.gender
    if (data.identifierType) updates.identifierType = data.identifierType
    if (data.insuranceNumber) updates.insuranceNumber = data.insuranceNumber

    if (Object.keys(updates).length > 0) {
      setFieldsValue(updates)
    }
  }
  const toggleEmergencyVisible = () => setEmergencyVisible(visible => !visible)

  return (
    <>
      {!forPatientQrCode && (
        <div className="divider-section no-margin-top">
          <Typography.Text strong>{t('calendar.patientDetails')}</Typography.Text>
        </div>
      )}
      <Row type="flex" gutter={16}>
        {!forPatientQrCode && (
          <Col md={colMediumSize} xs={24}>
            <Form.Item
              label={
                <Space>
                  {t('patients.patientId')}
                  {user.permissions.editPatientId && selectedPatient?.id ? (
                    <EditReferenceIdButton setFieldsValue={setFieldsValue} patient={selectedPatient} />
                  ) : null}
                </Space>
              }
              name="patientId"
              rules={[
                { max: 100 },
                {
                  required: requiredFields.includes('patientId'),
                  message: t('app.fieldRequired')
                }
              ]}
            >
              <Input
                addonBefore={
                  <Form.Item noStyle name="referenceId">
                    <InputNumber disabled readOnly />
                  </Form.Item>
                }
                autoComplete="given-name"
                maxLength={100}
                data-testid="patientForm-referenceId"
                onBlur={e => handleCheckDuplicatePatients(e, '*')}
              />
            </Form.Item>
          </Col>
        )}
        <Col md={nameMediumSize} xs={24}>
          <Form.Item
            label={t('calendar.firstName')}
            name="firstName"
            rules={[
              {
                required: true,
                message: t('common.nameRequired'),
                whitespace: true
              },
              { max: 100, message: t('auth.nameLong') },
              { min: 1, message: t('auth.nameLength') }
            ]}
          >
            <Input
              dir="auto"
              data-testid="patientForm-firstName"
              autoFocus
              autoComplete="given-name"
              maxLength={100}
              ref={autofocusInputRef}
            />
          </Form.Item>
        </Col>
        {hasMiddleName ? (
          <Col md={colMediumSize} xs={24}>
            <NameField
              formName="patientForm"
              autoComplete="middle-names"
              required
              label={t('calendar.middleNames')}
              fieldName="middleNames"
              min={1}
            />
          </Col>
        ) : null}
        <Col md={nameMediumSize} xs={24}>
          <NameField
            formName="patientForm"
            autoComplete="family-name"
            required
            label={t(forPatientQrCode && !hasMiddleName ? 'calendar.middleLastName' : 'calendar.lastName')}
            fieldName="lastName"
            min={1}
          />
        </Col>
        <Col md={colMediumSize} xs={24}>
          <PhoneField
            formName="patientForm"
            disabled={phoneDisabled}
            getFieldValue={getFieldValue}
            setFieldsValue={setFieldsValue}
            required
            initialValue={selectedPatient.phoneNumber}
            numberValid={selectedPatient.hasValidNumber}
            showValidation
            hasCountry={true}
            onBlur={e => handleCheckDuplicatePatients(e, '@')}
          />
        </Col>
        {visiblePatientFields.includes('phoneNumber2') && (
          <Col md={colMediumSize} xs={24}>
            <PhoneField
              formName="patientForm"
              getFieldValue={getFieldValue}
              setFieldsValue={setFieldsValue}
              suffixName="suffix2"
              fieldName="phoneNumber2"
              label={t('common.phoneNumber2')}
              required={requiredFields.includes('phoneNumber2')}
            />
          </Col>
        )}
        {forPatientQrCode && visiblePatientFields.includes('nationalId') && (
          <Col md={12} xs={24}>
            <NationalIdField
              requiredFields={requiredFields}
              identifierType={identifierType}
              companyCountry={companyCountry}
              nationality={nationality}
              forceNationalIDCheck={forceNationalIDCheck}
            />
          </Col>
        )}
        <Col md={colMediumSize} xs={24}>
          <CountryField
            name="country"
            formName="patientForm"
            label={t('common.country')}
            rules={[{ max: 50, message: t('auth.countryLong') }]}
            allowClear
            handleChangeCountry={handleChangeCountry}
          />
        </Col>
        {visiblePatientFields.includes('birthDate') && (
          <Col md={colMediumSize} sm={12}>
            {isBirthdate ? (
              <Form.Item
                label={t('common.birthDate')}
                name="birthDate"
                rules={[
                  {
                    required: requiredFields.includes('birthDate') || requiredFields.includes('age'),
                    message: t('app.fieldRequired')
                  }
                ]}
              >
                <MomentDatePicker
                  allowClear
                  data-testid="patientForm-birthDate"
                  format={{
                    format: selectorDateFormat(user)[1],
                    type: 'mask'
                  }}
                  className="full-width"
                  disabledDate={current => moment(current).diff(moment(new Date())) > 0}
                  onChange={handleChangeBirthDate}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item hidden name="birthDate">
                  <MomentDatePicker />
                </Form.Item>
                <Form.Item
                  name="age"
                  label={t('common.age')}
                  rules={[
                    {
                      required: requiredFields.includes('age'),
                      message: t('app.fieldRequired')
                    }
                  ]}
                >
                  <InputNumber
                    inputMode="numeric"
                    data-testid="patientForm-age"
                    min={0}
                    max={200}
                    precision={0}
                    onChange={handleChangeAge}
                    className="full-width"
                  />
                </Form.Item>
              </>
            )}
            <Form.Item hidden name="birthDay">
              <InputNumber data-testid="patientForm-birthDay" inputMode="decimal" />
            </Form.Item>
            {!forPatientQrCode ? (
              <Switch
                onChange={handleSwitchAge}
                checkedChildren={<CalendarOutlined />}
                unCheckedChildren={<CalendarOutlined />}
                size="small"
                checked={isBirthdate}
                className="age-birth-switch"
                // a required birth date is the exact one, which an age cannot give
                disabled={requiredFields.includes('birthDate')}
                data-testid="patientForm-switchAge"
              />
            ) : null}
          </Col>
        )}
        {visiblePatientFields.includes('patientTitle') && (
          <Col md={colMediumSize} xs={24}>
            <SelectWithNewOptions
              label={t('common.nameTitle')}
              name="patientTitle"
              allowClear
              setFieldsValue={setFieldsValue}
              options={patientTitles}
              formName="patientForm"
              rules={[
                { max: 50 },
                {
                  required: requiredFields.includes('patientTitle'),
                  message: t('app.fieldRequired')
                }
              ]}
            />
          </Col>
        )}
        {visiblePatientFields.includes('gender') && (
          <Col md={colMediumSize} xs={24}>
            <Form.Item
              label={t('calendar.gender')}
              name="gender"
              rules={[{ required: requiredFields.includes('gender'), message: t('app.fieldRequired') }]}
            >
              <Select
                data-testid="patientForm-gender"
                className="fixArrowSelection"
                showAction={['click', isMobile ? '' : 'focus']}
                allowClear
              >
                {genders.map(gender => (
                  <Select.Option value={gender} key={gender}>
                    {t(`calendar.${gender}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {visiblePatientFields.includes('email') && (
          <Col md={12} xs={24}>
            <EmailField formName="patientForm" required={requiredFields.includes('email')} />
          </Col>
        )}
        {visiblePatientFields.includes('address') && (
          <Col md={12} xs={24}>
            <Form.Item
              label={t('common.address')}
              name="address"
              rules={[
                { required: requiredFields.includes('address'), message: t('app.fieldRequired') },
                { max: 500, message: t('common.addressLong') }
              ]}
              validateStatus={!forPatientQrCode ? (longitude ? 'success' : address ? 'warning' : undefined) : undefined}
              help={!forPatientQrCode && !longitude && address ? t('app.locationError') : undefined}
            >
              <AddressField
                formName="patientForm"
                name="address"
                setFields={setFields}
                initialValue={selectedPatient.address || undefined}
                country={country}
                companyId={forPatientQrCode ? companyId : undefined}
                debounceTime={user?.company.debounceTime ?? debounceTime}
              />
              <Form.Item hidden name="longitude">
                <InputNumber data-testid="patientForm-longitude" inputMode="decimal" />
              </Form.Item>
              <Form.Item hidden name="latitude">
                <InputNumber data-testid="patientForm-latitude" inputMode="decimal" />
              </Form.Item>
              <Form.Item hidden name="state">
                <Input data-testid="patientForm-state" />
              </Form.Item>
            </Form.Item>
          </Col>
        )}
      </Row>
      {!forPatientQrCode && (
        <Row type="flex" gutter={16}>
          <Col md={12} xs={24}>
            <Form.Item
              rules={[{ required: requiredFields.includes('assignedPractitioner'), message: t('app.fieldRequired') }]}
              label={t('calendar.assignedPractitioner')}
              name="assignedPractitioner"
            >
              <Select
                className="fixArrowSelection"
                showSearch
                data-testid="patientForm-assignedPractitioner"
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                showAction={['click', isMobile ? '' : 'focus']}
                allowClear
                labelInValue
                onChange={e => e && setFieldsValue({ doctor: { key: e.key, label: e.label } })}
              >
                {branchData?.branchDetails?.users
                  ?.filter(user => user.isDoctor && !user.disabled)
                  .map(doctor => (
                    <Select.Option value={doctor.id} key={doctor.id}>
                      {doctor.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col md={12} xs={24}>
            <SearchField
              formName="patientForm"
              GET_QUERY={SEARCH_PRICE_LISTS_QUERY}
              label={t('patients.priceListG')}
              fieldName="priceList"
              dataName="searchPriceLists"
              allowClear
              filterPriceLists
              rules={[{ required: requiredFields.includes('priceList'), message: t('app.fieldRequired') }]}
            />
          </Col>
          <Col span={24}>
            <SearchField
              GET_QUERY={SEARCH_PATIENT_TAGS}
              label={t('app.tags')}
              fieldName="patientTags"
              dataName="searchPatientTags"
              multiple
              rules={[{ required: requiredFields.includes('tags'), message: t('app.fieldRequired') }]}
              setFieldsValue={setFieldsValue}
              getFieldValue={getFieldValue}
            />
          </Col>
          {visiblePatientFields.includes('marital') && (
            <Col md={colMediumSize} xs={24}>
              <Form.Item
                label={t('patients.marital')}
                name="marital"
                rules={[{ required: requiredFields.includes('marital'), message: t('app.fieldRequired') }]}
              >
                <Select
                  data-testid="patientForm-marital"
                  className="fixArrowSelection"
                  showAction={['click', isMobile ? '' : 'focus']}
                  allowClear
                >
                  {maritalStatuses.map(status => (
                    <Select.Option value={status} key={status}>
                      {t(`patients.${status}`)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
          {visiblePatientFields.includes('job') && (
            <Col md={colMediumSize} xs={24}>
              <SelectWithNewOptions
                label={t('common.job')}
                rules={[{ required: requiredFields.includes('job'), message: t('app.fieldRequired') }, { max: 100 }]}
                name="job"
                allowClear
                setFieldsValue={setFieldsValue}
                options={patientJobs}
              />
            </Col>
          )}
          <Col md={colMediumSize} xs={24}>
            <CountryField
              name="nationality"
              label={t('dashboardHome.patientsNationality')}
              rules={[{ required: requiredFields.includes('nationality'), message: t('app.fieldRequired') }]}
              allowClear
              handleChangeCountry={handleChangeNationality}
            />
          </Col>
          <Col lg={colMediumSize} xs={24}>
            <Form.Item
              label={t('settings.account.tax')}
              name="tax"
              // the server gives one who may not change taxes the tax their nationality comes with
              rules={[
                { required: requiredFields.includes('tax') && user.permissions.editInvoiceTax, message: t('app.fieldRequired') }
              ]}
            >
              <InputNumber
                inputMode="decimal"
                min={0}
                max={100}
                data-testid="patientForm-tax"
                // only who may change taxes sets a patient's own; for everyone else it follows their nationality
                disabled={!user.permissions.editInvoiceTax}
                addonAfter="%"
                parser={parser}
                formatter={formatter}
                precision={2}
                step={10}
                className="full-width"
              />
            </Form.Item>
          </Col>
          {user.permissions.detailsCreatedAt && (
            <Col md={8} xs={24}>
              <Form.Item name="createdAt" label={t('common.created')}>
                <MomentDatePicker
                  data-testid="patientForm-createdAt"
                  disabledDate={date => moment(date).isAfter(moment().endOf('day'))}
                  showTime={{ format: 'h:m a', use12Hours: true, defaultValue: moment() }}
                  allowClear={false}
                  needConfirm={false}
                  format="DD MMM YYYY hh:mm a"
                />
              </Form.Item>
            </Col>
          )}
          {selectedPatient.id ? (
            <Col md={8} xs={24}>
              <SearchField
                GET_QUERY={SEARCH_BRANCHES_QUERY}
                label={t('settings.inventory.branch')}
                fieldName="branch"
                dataName="searchBranches"
                permission="searchBranches"
              />
            </Col>
          ) : null}
          <Col md={8} xs={24}>
            <NationalIdField
              requiredFields={requiredFields}
              identifierType={identifierType}
              handleCheckDuplicate={handleCheckDuplicatePatients}
              companyCountry={companyCountry}
              nationality={nationality}
              forceNationalIDCheck={forceNationalIDCheck}
            />
          </Col>
          {hasDhsFeature && branchData?.branchDetails?.nphiesCode && !forPatientQrCode && user.permissions.checkDhsInsurance && (
            <Col md={8} xs={24} style={{ display: 'flex' }} >
              <DHSCheckInsurance
                user={user}
                form={form}
                nationalId={nationalId}
                identifierType={identifierType}
                nationality={nationality}
                branchId={selectedBranch}
                i18next={i18next}
                useTranslation={useTranslation}
                MoneyFormat={MoneyFormat}
                onReset={onReset}
                onSelect={handleInsuranceSelect}
                onPatientDataChange={handlePatientDataChange}
                disabled={!!identifierFormatError}
                variant="link"
                patient={{
                  ...selectedPatient,
                  ...form.getFieldsValue(true),
                  id: selectedPatient?.id || '',
                  firstName,
                  lastName,
                  nationalId,
                  identifierType,
                  branchId: selectedBranch,
                  birthDate,
                  gender,
                  marital,
                  job,
                  currentEligibility: selectedPatient.currentEligibility
                }}
                allowRecheck={true}
              />
            </Col>
          )}
        </Row>
      )}

      <Row type="flex" gutter={16}>
        {forPatientQrCode && visiblePatientFields.includes('job') && (
          <Col md={12} xs={24}>
            <SelectWithNewOptions
              label={t('common.job')}
              rules={[{ required: requiredFields.includes('job'), message: t('app.fieldRequired') }, { max: 100 }]}
              name="job"
              allowClear
              setFieldsValue={setFieldsValue}
              options={patientJobs}
            />
          </Col>
        )}
        {forPatientQrCode && visiblePatientFields.includes('marital') && (
          <Col md={12} xs={24}>
            <Form.Item
              label={t('patients.marital')}
              name="marital"
              rules={[{ required: requiredFields.includes('marital'), message: t('app.fieldRequired') }]}
            >
              <Select
                data-testid="patientForm-marital"
                className="fixArrowSelection"
                showAction={['click', isMobile ? '' : 'focus']}
                allowClear
              >
                {maritalStatuses.map(status => (
                  <Select.Option value={status} key={status}>
                    {t(`patients.${status}`)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {forPatientQrCode && visiblePatientFields.includes('nationality') && (
          <Col md={12} xs={24}>
            <CountryField
              name="nationality"
              label={t('dashboardHome.patientsNationality')}
              rules={[{ required: requiredFields.includes('nationality'), message: t('app.fieldRequired') }]}
              allowClear
            />
          </Col>
        )}
        {visiblePatientFields.includes('patientDetails') && (
          <Col span={24}>
            <Form.Item
              label={t('calendar.patientDetails')}
              name="patientDetails"
              rules={[
                { max: 1000, message: t('calendar.patientDetailsLong') },
                { required: requiredFields.includes('patientDetails'), message: t('app.fieldRequired') }
              ]}
            >
              <Input.TextArea
                dir="auto"
                data-testid="patientForm-details"
                rows={4}
                maxLength={1000}
                showCount
                placeholder={t('calendar.addDetails')}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Row type="flex" gutter={16}>
        {!forPatientQrCode && !selectedPatient.id && user?.company.extraSettings.allowTempPatient ? (
          <Col md={12} xs={24}>
            <Form.Item valuePropName="checked" name="isTempPatient">
              <Checkbox data-testid="patientForm-isTempPatient">
                <Tooltip className="tooltip-icon" title={t('sidebar.allowTempPatient')}>
                  <Typography.Text type="warning">
                    {t('app.isTempPatient')} <QuestionCircleOutlined twoToneColor="#faad14" />
                  </Typography.Text>
                </Tooltip>
              </Checkbox>
            </Form.Item>
          </Col>
        ) : null}
        {!forPatientQrCode && (
          <Col span={24}>
            <UploadFileButton setImages={setImages} images={images} single formVisible={formVisible} />
          </Col>
        )}
      </Row>
      {visiblePatientFields.includes('referralSource') && (
        <>
          <div className="divider-section">
            <Typography.Text strong>{t('patients.howYouHear')}</Typography.Text>
          </div>
          <ReferralSourceField
            companyId={companyId}
            hidePatient={!!forPatientQrCode}
            hideUser={!!forPatientQrCode}
            form={form}
            required={!!requiredFields.includes('referralSource')}
            referralDetailsRequired={!!requiredFields.includes('referralDetails')}
            referralDetailsVisible={!!visiblePatientFields.includes('referralDetails')}
          />
        </>
      )}
      {!forPatientQrCode && visiblePatientFields.some(w => w.includes('insurance')) && (
        <Row type="flex" gutter={16}>
          <Col span={24}>
            <div className="divider-section">
              <Typography.Text strong>{t('calendar.insuranceDetails')}</Typography.Text>
            </div>
          </Col>
          <InsuranceForm
            form={form}
            selectedPatient={selectedPatient}
            visiblePatientFields={visiblePatientFields}
            forPatientQrCode={forPatientQrCode}
            requiredPatientFields={requiredFields}
          />
        </Row>
      )}
      {!forPatientQrCode ? <PatientFamilyForm selectedPatient={selectedPatient} getFieldValue={getFieldValue} /> : null}
      {visiblePatientFields.includes('emergencyContact') && (
        <>
          <div className="divider-section has-button">
            <Button
              data-testid="patientForm-newFamily"
              icon={!emergencyShown ? <PlusOutlined /> : null}
              type="text"
              ghost
              disabled={!!emergencyShown}
              onClick={toggleEmergencyVisible}
            >
              <Typography.Text strong>{t('patients.emergencyContact')}</Typography.Text>
            </Button>
          </div>
          {emergencyShown ? (
            <Row type="flex" gutter={16}>
              <Col md={colMediumSize} xs={24}>
                <NameField
                  autoComplete="given-name"
                  fieldName="firstNameE"
                  label={t('calendar.firstName')}
                  min={0}
                  required={requiredFields.includes('emergencyContact')}
                  formName="patientForm"
                />
              </Col>
              <Col md={colMediumSize} xs={24}>
                <NameField
                  autoComplete="family-name"
                  fieldName="lastNameE"
                  label={t(forPatientQrCode ? 'calendar.middleLastName' : 'calendar.lastName')}
                  min={0}
                  required={requiredFields.includes('emergencyContact')}
                  formName="patientForm"
                />
              </Col>
              <Col md={colMediumSize} xs={24}>
                <PhoneField
                  getFieldValue={getFieldValue}
                  setFieldsValue={setFieldsValue}
                  suffixName="suffixE"
                  fieldName="phoneNumberE"
                  label={t('common.yourPhone')}
                  required={requiredFields.includes('emergencyContact')}
                  formName="patientForm"
                />
              </Col>
              <Col md={colMediumSize} xs={24}>
                <PhoneField
                  getFieldValue={getFieldValue}
                  setFieldsValue={setFieldsValue}
                  suffixName="suffix2E"
                  fieldName="phoneNumber2E"
                  label={t('common.phoneNumber2')}
                  formName="patientForm"
                />
              </Col>
              <Col md={colMediumSize} xs={24}>
                <Form.Item label={t('common.address')} name="addressE" rules={[{ max: 500, message: t('common.addressLong') }]}>
                  <Input data-testid="patientForm-addressE" maxLength={500} />
                </Form.Item>
              </Col>
              <Col md={colMediumSize} xs={24}>
                <SelectWithNewOptions
                  label={t('patients.relationship')}
                  rules={[{ max: 50 }]}
                  name="relationE"
                  allowClear
                  setFieldsValue={setFieldsValue}
                  options={patientRelations}
                  formName="patientForm"
                />
              </Col>
            </Row>
          ) : null}
        </>
      )}
    </>
  )
}

PatientCommonFields.propTypes = {
  form: PropTypes.object.isRequired,
  selectedPatient: PropTypes.object.isRequired,
  phoneDisabled: PropTypes.bool,
  forPatientQrCode: PropTypes.bool,
  companyCountry: PropTypes.string,
  formVisible: PropTypes.bool,
  images: PropTypes.array,
  setImages: PropTypes.func,
  requiredPatientFields: PropTypes.array,
  visiblePatientFields: PropTypes.array,
  referralSources: PropTypes.array,
  assignNewID: PropTypes.bool
}

export default PatientCommonFields
